import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import IPConfirmationModal from 'src/components/IPConfirmationModal';
import IPLoading from 'src/components/IPLoading';
import IPSimplePagination from 'src/components/IPSimplePagination';
import { useUrlPagination } from 'src/hooks/useUrlPagination';
import ProductTableRow from '../components/ProductTableRow';
import { DEFAULT_LIMIT, FAST_LISTING_CONFIRM_MESSAGE } from '../helpers/constants';
import { useBestSellingProducts, useClickToSellFastListing } from '../services';
import '../styles/styles.scss';

/**
 * Çok Satan Ürünler - Tıkla Sat
 * BI önerilen ürünleri tablo olarak listeler; tekli Tıkla Sat satır içinden, toplu işlem checkbox ile yapılır.
 */
const BestSellingProducts: React.FC = () => {
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const {
    page: urlPage,
    limit: urlLimit,
    setPage: setUrlPage,
    setLimit: setUrlLimit,
  } = useUrlPagination({
    defaultLimit: DEFAULT_LIMIT,
  });

  const { data, isLoading, isFetching, isError, error, refetch } = useBestSellingProducts({
    page: urlPage,
    limit: urlLimit,
  });

  const fastListingMutation = useClickToSellFastListing();

  const pageCount = data?.pageCount != null && data.pageCount > 0 ? data.pageCount : 1;
  const pageProductIds = data?.items?.map((item) => item.product.id) ?? [];
  const allPageSelected = pageProductIds.length > 0 && pageProductIds.every((id) => selectedProductIds.includes(id));
  const errorMessage = isError ? (error as any)?.message || 'Ürünler listelenemedi.' : null;

  useEffect(() => {
    handleClearSelection();
  }, [urlPage, urlLimit]);

  const handleToggleProduct = (productId: number) => {
    setSelectedProductIds((prev) => (prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]));
  };

  const handleSelectAll = () => {
    setSelectedProductIds(pageProductIds);
  };

  const handleClearSelection = () => {
    setSelectedProductIds([]);
  };

  const handleConfirmFastListing = () => {
    if (selectedProductIds.length === 0 || fastListingMutation.isLoading) return;

    fastListingMutation.mutate(selectedProductIds, {
      onSuccess: () => {
        handleClearSelection();
        setShowConfirmModal(false);
        refetch();
      },
    });
  };

  return (
    <>
      <div className="col-12 col-md-9 col-xl-8 mx-auto mt-4 mt-lg-8">
        <div className="card">
          <div className="card-body text-center">
            <div className="d-flex justify-content-center align-items-center">
              <h1 className="mb-0">Tıkla Sat</h1>
              <img src="/media/sidebar/click-to-sell-cursor.svg" alt="Logo" className="h-50px" />
            </div>
            <div className="mt-8 mb-6 fw-bold">
              <p className="mb-0">idefix&apos;te çok satan ürünleri seçerek hızlıca satışa başlayın.</p>
            </div>

            <div className="bg-light-warning rounded p-4 fs-7 text-gray-700 lh-lg" role="note">
              {FAST_LISTING_CONFIRM_MESSAGE}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10">
        {isLoading && !data && (
          <div className="card d-flex align-items-center w-100 mt-5 bg-transparent shadow-none">
            <div className="card-body">
              <IPLoading />
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="alert alert-danger mt-10" role="alert">
            {errorMessage}
          </div>
        )}

        {!isLoading && !errorMessage && !data?.items?.length && (
          <div className="card mt-10">
            <div className="card-body text-center text-muted py-15">Listelenecek önerilen ürün bulunamadı.</div>
          </div>
        )}

        {data?.items && data.items.length > 0 && (
          <>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-5">
              <div className="d-flex align-items-center gap-3">
                <button type="button" className="btn btn-sm btn-light-primary" onClick={allPageSelected ? handleClearSelection : handleSelectAll}>
                  {allPageSelected ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                </button>
                {selectedProductIds.length > 0 && <span className="fs-7 fw-bold text-muted">{selectedProductIds.length} ürün seçildi</span>}
              </div>
              {selectedProductIds.length > 1 && (
                <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowConfirmModal(true)}>
                  Seçilenleri Tıkla Sat
                </button>
              )}
            </div>

            <div className={clsx('card overlay', isFetching && 'overlay-block')}>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3 mb-0 best-selling-products-table">
                    <thead>
                      <tr className="fw-bolder text-muted bg-light">
                        <th className="ps-3 sticky-column checkbox">
                          <div className="form-check form-check-custom form-check-solid form-check-sm">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={allPageSelected}
                              onChange={allPageSelected ? handleClearSelection : handleSelectAll}
                              aria-label="Tümünü seç"
                            />
                          </div>
                        </th>
                        <th className="min-w-200px sticky-column product-name">Ürün Adı</th>
                        <th className="min-w-125px">Kategori</th>
                        <th className="min-w-100px">Marka</th>
                        <th className="mw-125px text-center">Barkod</th>
                        <th className="min-w-150px text-center">
                          Satıcı Stok Kodu<span className="text-danger">*</span>
                        </th>
                        <th className="min-w-100px text-center">
                          Stok Miktarı<span className="text-danger">*</span>
                        </th>
                        <th className="min-w-100px text-center">
                          idefix Satış Fiyatı<span className="text-danger">*</span>
                        </th>
                        <th className="min-w-100px text-center">Piyasa Satış Fiyatı</th>
                        <th className="min-w-125px pe-3 text-center">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((item) => (
                        <ProductTableRow
                          key={item.id}
                          product={item.product}
                          isSelected={selectedProductIds.includes(item.product.id)}
                          disableClickToSell={selectedProductIds.length > 1}
                          onToggle={handleToggleProduct}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {isFetching && (
                <div className="overlay-layer rounded bg-dark bg-opacity-5 z-index-3">
                  <IPLoading />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="d-flex justify-content-center align-items-center mt-5 gap-4">
        {(data?.totalCount ?? 0) > 0 && (
          <>
            <IPSimplePagination
              containerClassName="pagination bg-white rounded"
              currentPage={urlPage}
              pageCount={pageCount}
              pageLinkClassName="page-link btn btn-icon btn-sm btn-light mr-2 my-1"
              siblingCount={1}
              onPageChange={setUrlPage}
            />

            <div className="d-flex justify-content-end">
              <select value={urlLimit} name="limit" onChange={(e) => setUrlLimit(Number(e.target.value))} data-control="select2" data-hide-search="true" className="form-select">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </>
        )}
      </div>

      <IPConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmFastListing}
        title="Toplu Tıkla Sat"
        message={FAST_LISTING_CONFIRM_MESSAGE}
        confirmButtonText="Onayla"
        cancelButtonText="Vazgeç"
        isLoading={fastListingMutation.isLoading}
      />
    </>
  );
};

export { BestSellingProducts };
export default BestSellingProducts;
